// src/app/components/analytics/series/performance-series-view.tsx
//
// The longitudinal Performance view, built on the validated series contract but
// laid out exactly like the current Performance module: KPI cards on top, then
// one chart per metric stacked with dividers, all scrollable, identical
// formatting. Every metric the envelope carries (AI Index, Mention, Best-in-
// Category, #1, plus the newer research-variable / persona / competitive layers)
// gets its own chart, in a stable order.
//
// When the data is coming from the synthetic fixture (the local preview default)
// a clear "Synthetic data" pill is shown so it can never be mistaken for a live
// client measurement.

import { Fragment, useEffect, useMemo, useState } from "react";
import { BarChart3 } from "lucide-react";
import { SeriesChartPanel } from "./series-chart-panel";
import { VariableLens } from "./variable-lens";
import { fetchSeries } from "@/app/services/performance-series/source";
import { useSeriesSourceMode } from "@/app/services/performance-series/use-source-mode";
import { deriveLenses } from "@/app/services/performance-series/registry";
import type { NormalizedEnvelope } from "@/app/services/performance-series/types";
import { getPerformanceEvents, type PerformanceEvent } from "@/app/data/performance-events";

interface Props {
  brandName?: string;
  projectionDate?: string | null;
  onNavigateToInsights?: () => void;
}

// Synthetic timeline events for the fixture preview: past interventions/content
// plus FORWARD-looking scheduled measurement runs (type "scan"), which drive the
// ramped forward projection on the charts. Real brands use getPerformanceEvents.
function fixtureEvents(): PerformanceEvent[] {
  const now = Date.now();
  const day = 86_400_000;
  const iso = (d: number) => new Date(now + d * day).toISOString().slice(0, 10);
  return [
    { id: "ev-wave1", date: iso(-24), title: "Wave 1: Evidence packages deployed", description: "First set of AI-visibility interventions shipped.", type: "launch" },
    { id: "ev-content", date: iso(-12), title: "Content published", description: "Practice-facing value-proposition content went live.", type: "content" },
    { id: "ev-scan0", date: iso(-2), title: "Measurement run", description: "Re-measured across all models.", type: "scan" },
    { id: "ev-scan1", date: iso(14), title: "Measurement run · D30", description: "Scheduled re-measurement. First retrieval-augmented read on ChatGPT.", type: "scan" },
    { id: "ev-scan2", date: iso(45), title: "Deep run · D60", description: "Weight-window read across ChatGPT and Claude.", type: "scan" },
  ];
}

export function PerformanceSeriesView({ brandName, projectionDate, onNavigateToInsights }: Props) {
  const [envelope, setEnvelope] = useState<NormalizedEnvelope | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const sourceMode = useSeriesSourceMode();

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    fetchSeries(brandName || "")
      .then((res) => { if (!cancelled) setEnvelope(res.envelope); })
      .catch(() => { if (!cancelled) setEnvelope(null); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [brandName]);

  // Data-driven: a lens only appears if its layer has data. Personas and
  // competitive are simply absent from the data today, so they do not render;
  // they will appear automatically when real data for them exists.
  // Removed from the Performance page 7/27:
  //   - personas + competitive: their layers exist but do not yet tell a
  //     client-actionable story here (Deep Signal remains their home).
  //   - funnel.number_one: primary recommendation is the reporting endpoint;
  //     #1 choice is retired from client-facing surfaces.
  // Re-enable any of these by deleting its clause below.
  const lenses = useMemo(
    () => (envelope
      ? deriveLenses(envelope).filter((l) =>
          l.group !== "personas" && l.group !== "competitive" && l.key !== "funnel.number_one")
      : []),
    [envelope],
  );
  const events = useMemo(() => {
    // Fixture preview uses synthetic events (incl. forward-looking runs) so the
    // event pins + ramped projection are visible; real data uses the Blueprint.
    if (sourceMode === "fixture") return fixtureEvents();
    try { return getPerformanceEvents(brandName); } catch { return []; }
  }, [brandName, sourceMode]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 240 }}>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full animate-pulse" style={{ backgroundColor: "var(--alpha-teal)" }} />
          <span style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>Loading performance data…</span>
        </div>
      </div>
    );
  }

  if (!envelope || lenses.length === 0) {
    return (
      <div className="flex items-center justify-center px-4" style={{ minHeight: 220 }}>
        <div className="text-center" style={{ maxWidth: 360 }}>
          <BarChart3 className="w-9 h-9 mx-auto mb-3" style={{ color: "var(--muted-foreground)" }} />
          <h4 style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)", marginBottom: 6 }}>
            No measurement data yet
          </h4>
          <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)", lineHeight: 1.6 }}>
            {brandName ? `We don't have measurement runs for ${brandName} yet.` : "Select a brand to see its performance data."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 20 }}>
      {/* KPI strip + synthetic-data pill now live at the top of the page
          (PerformanceKpiStrip), above the Research Analyst card. */}

      {/* One chart per metric, stacked with dividers (matches the current layout) */}
      {lenses.map((lens, i) => (
        <Fragment key={lens.key}>
          {i > 0 && <div style={{ height: 1, backgroundColor: "var(--border)" }} />}
          {lens.group === "variables" ? (
            // Variables ONLY: ranked list + sparklines + drill-in. Every other
            // lens keeps the standard time-series chart, untouched.
            <VariableLens
              envelope={envelope}
              lens={lens}
              brandName={brandName}
              // "Next measurement" marker suppressed: the schedule feed this
              // date came from is the MONITORING cadence, not a scheduled Deep
              // Signal measurement, so drawing it on measurement charts claimed
              // a run nobody scheduled. Re-plumb when a real measurement
              // schedule exists.
              projectionDate={null}
              events={events}
              onNavigateToInsights={onNavigateToInsights}
            />
          ) : (
            <SeriesChartPanel
              envelope={envelope}
              lens={lens}
              brandName={brandName}
              projectionDate={null}
              events={events}
              onNavigateToInsights={onNavigateToInsights}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
}
